import { useMemo } from 'react';
import i18n from './i18n';
import { useNavigate } from 'react-router-dom';
import { useAuthToken, useCurrentUser } from './auth';
import { ORGANIZATION_EDIT_PERMISSION } from './constants/permissions';

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';

export const useApi = () => {
  const token = useAuthToken();
  const navigate = useNavigate();
  const { organizationSetupRequired, permissions, currentUser, impersonatedOrgId } =
    useCurrentUser();
  const baseOrgId = currentUser?.org_id ?? null;
  const orgOverrideHeaderValue = impersonatedOrgId && impersonatedOrgId !== baseOrgId ? impersonatedOrgId : null;
  const hasOrganizationEditPermission = useMemo(
    () => permissions.includes(ORGANIZATION_EDIT_PERMISSION),
    [permissions],
  );

  return useMemo(() => {
    const organizationSetupPath = '/organization/setup';
    const organizationSetupInfoPath = '/organization/setup-info';

    const handleOrganizationSetupRedirect = () => {
      if (!organizationSetupRequired) return;
      const target = hasOrganizationEditPermission ? organizationSetupPath : organizationSetupInfoPath;
      if (import.meta.env.DEV) console.warn('403; redirecting to org setup flow:', target);
      navigate(target, { replace: true });
    };

    const parseResponseBody = async (response: Response) => {
      if (response.status === 204) return null;

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
        return response.json().catch(() => null);
      }
      return response.text().catch(() => null);
    };

    const processErrorResponse = async (response: Response) => {
      const data = await parseResponseBody(response);

      if (response.status === 403) {
        const problemCode = data && typeof data === 'object' ? (data as any).code : undefined;
        if (problemCode === 'NO_CREDITS') {
          const err: any = new Error('NO_CREDITS');
          err.status = 403;
          err.code = 'NO_CREDITS';
          err.detail = (data as any)?.detail;
          throw err;
        }
        if (problemCode === 'NO_CAPACITY') {
          const friendly = i18n.t('errors.noCapacity');
          const err: any = new Error(friendly);
          err.status = 403;
          err.code = 'NO_CAPACITY';
          err.detail = friendly;
          err.problem = data;
          throw err;
        }

        if (organizationSetupRequired) handleOrganizationSetupRedirect();
      }

      const detail = data && typeof data === 'object' ? (data as any).detail : undefined;
      const message = typeof detail === 'string' ? detail : detail?.message;
      const err: any = new Error(message || `Request failed: ${response.status}`);
      err.status = response.status;
      if (data && typeof data === 'object') {
        err.code = (data as any).code;
        err.detail = (data as any).detail;
        err.problem = data;
      }
      throw err;
    };

    const processSuccessResponse = async (response: Response) => {
      if (response.status === 204) return null;
      return parseResponseBody(response);
    };

    const buildHeaders = (
      initHeaders: HeadersInit | undefined,
      options: { json?: boolean; formData?: boolean; tokenOverride?: string },
    ) => {
      const headers = new Headers(initHeaders);

      const authToken = options.tokenOverride ?? token;
      if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
      if (orgOverrideHeaderValue) headers.set('X-Org-Override-Id', orgOverrideHeaderValue);

      if (options.formData) {
        headers.delete('Content-Type');
      } else if (options.json && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    };

    const executeRequest = async (
      path: string,
      init: RequestInit,
      options: { json?: boolean; formData?: boolean; tokenOverride?: string },
    ) => {
      const includeCredentials = path === '/organizations/me';
      return fetch(`${API_BASE_URL}${path}`, {
        ...init,
        credentials: includeCredentials ? 'include' : init.credentials,
        headers: buildHeaders(init.headers, options),
      });
    };

    const requestResponse = async (
      path: string,
      init: RequestInit = {},
      options: { json?: boolean; formData?: boolean } = {},
      retryCount = 0,
    ): Promise<Response> => {
      let response = await executeRequest(path, init, options);

      if (response.status === 401 && retryCount === 0) {
        if (import.meta.env.DEV) {
          console.log('API request got 401, token may have expired. Retrying...');
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));

        const newToken = localStorage.getItem(
          'oidc.user:' + window.location.origin + ':' + (import.meta as any).env.VITE_OIDC_CLIENT_ID,
        );

        if (newToken) {
          const userData = JSON.parse(newToken);
          if (userData?.access_token) {
            response = await executeRequest(path, init, { ...options, tokenOverride: userData.access_token });
          }
        }
      }

      if (!response.ok) {
        await processErrorResponse(response);
      }

      return response;
    };

    const request = async (
      path: string,
      init: RequestInit = {},
      options: { json?: boolean; formData?: boolean } = {},
    ): Promise<any> => processSuccessResponse(await requestResponse(path, init, options));

    return {
      get: (path: string) => request(path),
      getResponse: (path: string, init?: RequestInit) => requestResponse(path, init),
      post: (path: string, body: unknown, init?: RequestInit) =>
        request(path, { method: 'POST', body: JSON.stringify(body), ...(init || {}) }, { json: true }),
      postFormData: (path: string, body: FormData, init?: RequestInit) =>
        request(path, { method: 'POST', body, ...(init || {}) }, { formData: true }),
      put: (path: string, body: unknown, init?: RequestInit) =>
        request(path, { method: 'PUT', body: JSON.stringify(body), ...(init || {}) }, { json: true }),
      patch: (path: string, body: unknown, init?: RequestInit) =>
        request(path, { method: 'PATCH', body: JSON.stringify(body), ...(init || {}) }, { json: true }),
      del: (path: string, init?: RequestInit) => request(path, { method: 'DELETE', ...(init || {}) }),
    };
  }, [
    token,
    navigate,
    organizationSetupRequired,
    hasOrganizationEditPermission,
    orgOverrideHeaderValue,
  ]);
};
